/**
 * API endpoint definitions
 */

import { apiClient } from './client';
import { DomainType } from '../types';
import type {
  Metadata,
  MapData,
  SearchData,
  AIOSearchData,
  OrganizationListResponse,
  TermsResponse,
  RankingResponse,
  BubbleDataResponse,
  AIOOverviewResponse,
  CompareResponse,
  OrgIdsWithDataResponse,
  ActorOverviewResponse,
  ActorFieldsResponse,
  ActorFieldDetailResponse,
  TermByCityResponse,
  ActorDetailResponse,
  ComparisonGraphResponse,
  SuggestionRequest,
  SuggestionResponse,
} from '../types';

// Common endpoints
export const metadataAPI = {
  getMetadata: () => apiClient.get<Metadata>('/metadata'),
};

export const suggestionsAPI = {
  submit: (data: SuggestionRequest) =>
    apiClient.post<SuggestionResponse>('/suggestions', data),
};

export interface SuggestedTopicResponse {
  success: boolean;
  message: string;
}

export const suggestedTopicsAPI = {
  submit: (topicName: string, email: string, domain: DomainType) =>
    apiClient.post<SuggestedTopicResponse>('/suggested-topics', {
      topic_name: topicName,
      email,
      domain,
    }),
};

// Domain-specific API factory
export const createDomainAPI = (domain: DomainType) => {
  const getPrefix = () => {
    if (domain.startsWith('media')) {
      const subdomain = domain.replace('media', '');
      return `/media/${subdomain}`;
    }
    return `/${domain}`;
  };

  const prefix = getPrefix();

  return {
    getMapData: (term: string) =>
      apiClient.get<MapData>(`${prefix}/map-data`, { params: { term } }),

    /** Get search results for a single city+term (for Topics Ranking city click). */
    getCitySearchResults: (term: string, city: string) =>
      apiClient.get<SearchData[]>(`${prefix}/search-data`, { params: { term, city, limit: 1 } }),

    getOrganizations: () =>
      apiClient.get<OrganizationListResponse>(`${prefix}/organizations`),

    getTerms: (aio = false) =>
      apiClient.get<TermsResponse>(`${prefix}/terms`, {
        params: aio ? { aio: true } : undefined,
      }),

    getRankings: (field?: string, limit?: number) =>
      apiClient.get<RankingResponse>(`${prefix}/rankings`, {
        params: { field, limit },
      }),

    getBubbleData: (field: string) =>
      apiClient.get<BubbleDataResponse>(`${prefix}/bubble-data`, {
        params: { field },
      }),

    getAIOOverview: () =>
      apiClient.get<AIOOverviewResponse>(`${prefix}/aio-overview`),

    getAIORankings: (field?: string, limit?: number) =>
      apiClient.get<RankingResponse>(`${prefix}/aio-rankings`, {
        params: { field, limit },
      }),

    getAIOBubbleData: (field?: string) =>
      apiClient.get<BubbleDataResponse>(`${prefix}/aio-bubble-data`, {
        params: { field },
      }),

    getAIOMapData: (term: string) =>
      apiClient.get<MapData>(`${prefix}/aio-map-data`, { params: { term } }),

    /** Get AI Overview citation data for a single city+term (for Geneva city click). */
    getAIOCitySearchResults: (term: string, city: string) =>
      apiClient.get<AIOSearchData[]>(`${prefix}/aio-search-data`, {
        params: { term, city, limit: 1 },
      }),

    compareOrganizations: (orgIds: number[]) =>
      apiClient.get<CompareResponse>(`${prefix}/compare`, {
        params: { org_ids: orgIds },
        paramsSerializer: { indexes: null }, // Send as org_ids=1&org_ids=2
      }),

    // Actor Analysis (1:1 with Dash compare view)
    getOrgIdsWithData: (aio = false) =>
      apiClient.get<OrgIdsWithDataResponse>(`${prefix}/actor-analysis/org-ids-with-data`, {
        params: aio ? { aio: true } : undefined,
      }),

    getActorOverview: (orgId: number, aio = false) =>
      apiClient.get<ActorOverviewResponse>(`${prefix}/actor-analysis/overview`, {
        params: { org_id: orgId, ...(aio ? { aio: true } : {}) },
      }),

    getActorFields: (orgId: number, aio = false) =>
      apiClient.get<ActorFieldsResponse>(`${prefix}/actor-analysis/fields`, {
        params: { org_id: orgId, ...(aio ? { aio: true } : {}) },
      }),

    getActorFieldDetail: (orgId: number, field: string, aio = false) =>
      apiClient.get<ActorFieldDetailResponse>(`${prefix}/actor-analysis/field-detail`, {
        params: { org_id: orgId, field, ...(aio ? { aio: true } : {}) },
      }),

    getTermByCity: (orgId: number, term: string, aio = false) =>
      apiClient.get<TermByCityResponse>(`${prefix}/actor-analysis/term-by-city`, {
        params: { org_id: orgId, term, ...(aio ? { aio: true } : {}) },
      }),

    getActorDetail: (orgId: number, field?: string, term?: string, aio = false) =>
      apiClient.get<ActorDetailResponse>(`${prefix}/actor-analysis/actor-detail`, {
        params: {
          org_id: orgId,
          ...(field != null && field !== '' && { field }),
          ...(term != null && term !== '' && { term }),
          ...(aio ? { aio: true } : {}),
        },
      }),

    getComparisonGraph: (orgId1: number, orgId2: number, aio = false) =>
      apiClient.get<ComparisonGraphResponse>(`${prefix}/actor-analysis/comparison`, {
        params: { org_id_1: orgId1, org_id_2: orgId2, ...(aio ? { aio: true } : {}) },
      }),
  };
};

// Export pre-configured domain APIs
export const diplomaticAPI = createDomainAPI('diplomatic');
export const techAPI = createDomainAPI('tech');
export const mediaDiplomaticAPI = createDomainAPI('mediadiplomatic');
export const mediaDigitalAPI = createDomainAPI('mediadigital');
export const genevaAPI = createDomainAPI('geneva');

// Helper to get API for any domain
export const getAPIForDomain = (domain: DomainType) => {
  return createDomainAPI(domain);
};
